/**
 * @file Admin-owned Compliance category Settings participant.
 */

import {
    appData,
    tempConfigBaselineData,
    tempConfigData,
} from "../../app/state/state.js";
import {
    bindNativeFieldBehavior,
    FIELD_LABEL_CLASSES,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    REQUIRED_MARK_HTML,
    validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    destroyRegisteredControlsIn,
    focusFirstRegisteredControlError,
    validateRegisteredControls,
} from "../../shared/ui/controls/controlCore.js";
import {createSelectControl} from "../../shared/ui/controls/selectControl.js";
import {openConfirmationModal} from "../../shared/ui/confirmationModal.js";
import {escapeHTML} from "../../shared/utils/contentUtils.js";
import {
    changeComplianceCategoryLifecycle,
    initializeComplianceProtectedCategories,
    saveComplianceCategoryConfig,
} from "../compliance/complianceApi.js";

const EMPTY_USED_CATEGORY_CHOICE_REQUIRED =
    "empty_used_category_choice_required";
const PROTECTED_CATEGORY_DEFINITIONS = Object.freeze([
    Object.freeze({systemKey: "people", name: "People"}),
    Object.freeze({systemKey: "assets", name: "Assets"}),
]);

let showInactive = false;
let renderGeneration = 0;
let updateSharedSaveButton = () => {};
let isCurrentSettingsOwner = () => true;
let protectedCategoryInitializationDrafts = new Map();

function getDraftCategories() {
    return Array.isArray(tempConfigData?.complianceCategories) ?
        tempConfigData.complianceCategories : [];
}

function getBaselineCategories() {
    return Array.isArray(tempConfigBaselineData?.complianceCategories) ?
        tempConfigBaselineData.complianceCategories : [];
}

function ensureDraftCategories() {
    if (!tempConfigData) {return null;}
    if (!Array.isArray(tempConfigData.complianceCategories)) {
        tempConfigData.complianceCategories = [];
    }
    return tempConfigData.complianceCategories;
}

function getOrderedCategories(categories = getDraftCategories()) {
    return categories.map((category, index) => ({
        category,
        order: Number.isFinite(Number(category?.order)) ?
            Number(category.order) :
            index,
    })).sort((left, right) =>
        left.order - right.order ||
        String(left.category?.id || "")
            .localeCompare(String(right.category?.id || "")),
    );
}

function isProtectedCategory(category) {
    return category?.kind === "system" &&
        ["people", "assets"].includes(category?.systemKey);
}

function getMissingProtectedCategoryDefinitions() {
    return PROTECTED_CATEGORY_DEFINITIONS.filter(({systemKey}) =>
        !getDraftCategories().some((category) =>
            category?.kind === "system" &&
            category?.systemKey === systemKey,
        ),
    );
}

function syncProtectedCategoryInitializationDrafts() {
    const missingKeys = new Set(
        getMissingProtectedCategoryDefinitions()
            .map(({systemKey}) => systemKey),
    );
    protectedCategoryInitializationDrafts =
        new Map([...protectedCategoryInitializationDrafts]
            .filter(([systemKey]) => missingKeys.has(systemKey)));
    missingKeys.forEach((systemKey) => {
        if (!protectedCategoryInitializationDrafts.has(systemKey)) {
            protectedCategoryInitializationDrafts.set(systemKey, {
                systemKey,
                teamId: "",
                order: "",
                dueSoonDays: "",
            });
        }
    });
}

function ownsCurrentRender(container, generation) {
    return generation === renderGeneration &&
        container?.isConnected &&
        isCurrentSettingsOwner() &&
        Boolean(container.querySelector("[data-compliance-settings]"));
}

function findCategory(categoryId) {
    return getDraftCategories().find((entry) =>
        entry?.id === categoryId,
    ) || null;
}

function isPersisted(categoryId) {
    return getBaselineCategories().some((entry) =>
        entry?.id === categoryId,
    );
}

function markDirty() {
    updateSharedSaveButton(true);
}

function buildExpectedCategoryState(category) {
    return {
        categoryId: category.id,
        name: category.name,
        order: category.order,
        isActive: category.isActive === true,
        hasBeenUsed: category.hasBeenUsed === true,
        teamId: category.teamId,
        dueSoonDays: category.dueSoonDays,
    };
}

function editableCategorySnapshot(category) {
    return {
        name: String(category?.name || "").trim(),
        order: Number(category?.order),
        teamId: String(category?.teamId || ""),
        dueSoonDays: category?.dueSoonDays,
    };
}

function snapshotsEqual(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
}

function cloneValue(value) {
    return value === undefined ? undefined : structuredClone(value);
}

function reconcileAuthoritativeCategories(
    authoritative,
    options = {},
) {
    if (!tempConfigData || !tempConfigBaselineData ||
        !Array.isArray(authoritative)) {return;}
    const previousBaseline = getBaselineCategories();
    const currentDraft = getDraftCategories();
    const baselineById = new Map(previousBaseline.map((entry) => [
        entry.id,
        entry,
    ]));
    const deletedId = options.action === "delete" ?
        options.categoryId :
        "";
    const temporaryId = options.temporaryId || "";
    const createdId = options.createdId || "";
    const nextDraft = authoritative.map((entry) => structuredClone(entry));
    const nextById = new Map(nextDraft.map((entry, index) => [
        entry.id,
        {entry, index},
    ]));

    currentDraft.forEach((draftCategory) => {
        if (draftCategory.id === deletedId) {return;}
        if (draftCategory.id === temporaryId && createdId) {
            const target = nextById.get(createdId);
            if (!target) {return;}
            const merged = {
                ...target.entry,
                ...structuredClone(draftCategory),
                id: createdId,
                kind: target.entry.kind,
                systemKey: target.entry.systemKey,
                isActive: target.entry.isActive,
                hasBeenUsed: target.entry.hasBeenUsed,
            };
            nextDraft[target.index] = merged;
            return;
        }
        const baseline = baselineById.get(draftCategory.id);
        const target = nextById.get(draftCategory.id);
        if (!baseline) {
            if (draftCategory.id !== temporaryId) {
                nextDraft.push(structuredClone(draftCategory));
            }
            return;
        }
        if (!target) {return;}
        const merged = {...target.entry};
        const keys = new Set([
            ...Object.keys(baseline),
            ...Object.keys(draftCategory),
        ]);
        keys.forEach((key) => {
            if (JSON.stringify(draftCategory[key]) ===
                JSON.stringify(baseline[key])) {return;}
            if (Object.hasOwn(draftCategory, key)) {
                merged[key] = cloneValue(draftCategory[key]);
            } else {
                delete merged[key];
            }
        });
        if (draftCategory.id === options.categoryId &&
            options.action !== "delete") {
            merged.isActive = target.entry.isActive === true;
        }
        nextDraft[target.index] = merged;
    });

    tempConfigData.complianceCategories = nextDraft;
    tempConfigBaselineData.complianceCategories =
        structuredClone(authoritative);
    if (appData.config) {
        appData.config.complianceCategories =
            structuredClone(authoritative);
    }
}

function renderToggle() {
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input type="checkbox" class="${NATIVE_CHECKBOX_CLASSES}"
                data-compliance-settings-show-inactive
                ${showInactive ? "checked" : ""}>
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0"
                    data-native-checkbox-icon>check</span>
            </span>
            <span class="text-sm text-gray-600">Show inactive</span>
        </label>
    `;
}

function getTeamOptions() {
    return (Array.isArray(appData.teams) ? appData.teams : [])
        .map((team, index) => ({
            value: team.id,
            label: team.name || team.id,
            order: Number.isFinite(Number(team.order)) ?
                Number(team.order) :
                index,
        }));
}

function renderMoveButton(category, direction, disabled) {
    const label = direction === "up" ?
        "Move category up" :
        "Move category down";
    return `
        <button type="button" data-compliance-category-move="${direction}"
            data-compliance-category-id="${escapeHTML(category.id)}"
            class="standard-action-icon-btn !p-0 disabled:cursor-not-allowed disabled:opacity-25"
            aria-label="${label}" ${disabled ? "disabled" : ""}>
            <span class="material-symbols-outlined">
                ${direction === "up" ? "expand_less" : "expand_more"}
            </span>
        </button>
    `;
}

function renderCategoryAction(category) {
    if (isProtectedCategory(category)) {
        return `
            <span class="inline-flex items-center text-xs text-gray-500"
                title="People and Assets are fixed categories">
                Fixed
            </span>
        `;
    }
    if (category.hasBeenUsed !== true) {
        return `
            <button type="button" data-compliance-category-action="delete"
                class="settings-config-delete"
                aria-label="Delete unused Compliance category">
                <span class="material-symbols-outlined text-lg">delete</span>
            </button>
        `;
    }
    const action = category.isActive === false ? "restore" : "archive";
    return `
        <button type="button" data-compliance-category-action="${action}"
            class="settings-config-archive ${action === "restore" ? "settings-config-restore" : ""}"
            aria-label="${action === "restore" ? "Unarchive" : "Archive"} Compliance category">
            <span class="material-symbols-outlined text-lg">
                ${action === "restore" ? "unarchive" : "archive"}
            </span>
        </button>
    `;
}

function renderCategoryRows() {
    const visible = getOrderedCategories().filter(({category}) =>
        showInactive || category.isActive === true);
    if (!visible.length) {
        return `
            <p class="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
                ${showInactive ?
                    "No Compliance categories have been configured." :
                    "No active Compliance categories. Add a category or show inactive categories."}
            </p>
        `;
    }
    return visible.map(({category}, index) => {
        const active = category.isActive === true;
        const protectedCategory = isProtectedCategory(category);
        return `
            <div class="grid grid-cols-[auto_minmax(0,1.4fr)_minmax(9rem,1fr)_7rem_auto] items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 max-md:grid-cols-[auto_minmax(0,1fr)_auto] ${active ? "" : "opacity-70"}"
                data-compliance-category-id="${escapeHTML(category.id)}">
                <div class="flex flex-col" aria-label="Category order controls">
                    ${renderMoveButton(category, "up", index === 0 || !active)}
                    ${renderMoveButton(category, "down", index === visible.length - 1 || !active)}
                </div>
                <div class="min-w-0">
                    <label class="${FIELD_LABEL_CLASSES} mb-1"
                        for="compliance-category-name-${escapeHTML(category.id)}">Category name${REQUIRED_MARK_HTML}</label>
                    <input id="compliance-category-name-${escapeHTML(category.id)}"
                        data-compliance-category-name maxlength="80" required
                        value="${escapeHTML(category.name || "")}"
                        ${active && !protectedCategory ? "" : "disabled"}
                        class="w-full rounded-md border border-gray-200 px-2 py-1 text-sm">
                    ${protectedCategory ?
                        '<p class="mt-1 text-xs text-gray-500">System name is fixed.</p>' :
                        ""}
                </div>
                <div class="min-w-0 max-md:col-start-2">
                    <label class="${FIELD_LABEL_CLASSES} mb-1">Team${REQUIRED_MARK_HTML}</label>
                    <div data-compliance-category-team
                        data-compliance-category-team-id="${escapeHTML(category.id)}"></div>
                </div>
                <div class="min-w-0 max-md:col-start-2">
                    <label class="${FIELD_LABEL_CLASSES} mb-1"
                        for="compliance-category-due-${escapeHTML(category.id)}">Due soon days${REQUIRED_MARK_HTML}</label>
                    <input id="compliance-category-due-${escapeHTML(category.id)}"
                        data-compliance-category-due type="number" min="0"
                        max="365" step="1" required
                        value="${escapeHTML(String(category.dueSoonDays))}"
                        ${active ? "" : "disabled"}
                        class="w-full rounded-md border border-gray-200 px-2 py-1 text-sm">
                </div>
                <div class="flex min-h-8 items-center justify-end max-md:col-start-3 max-md:row-start-1">
                    ${renderCategoryAction(category)}
                </div>
            </div>
        `;
    }).join("");
}

function renderProtectedCategoryInitialization() {
    syncProtectedCategoryInitializationDrafts();
    const missing = getMissingProtectedCategoryDefinitions();
    if (!missing.length) {return "";}
    return `
        <section class="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4"
            data-compliance-protected-initialization>
            <h4 class="text-sm font-semibold text-gray-900">
                Initialise protected categories
            </h4>
            <p class="mt-1 text-sm text-gray-600">
                Select the team, display order, and due-soon threshold for each
                missing fixed category. No tenant defaults are inferred.
            </p>
            <div class="mt-4 space-y-3">
                ${missing.map(({systemKey, name}) => {
                    const draft =
                        protectedCategoryInitializationDrafts.get(systemKey);
                    return `
                        <div class="grid grid-cols-[minmax(0,1fr)_minmax(9rem,1fr)_7rem_7rem] items-end gap-2 rounded-md border border-gray-200 bg-white p-3 max-md:grid-cols-1"
                            data-compliance-protected-row="${systemKey}">
                            <div>
                                <span class="${FIELD_LABEL_CLASSES} mb-1">
                                    Category
                                </span>
                                <p class="flex min-h-9 items-center rounded-md bg-gray-100 px-3 text-sm text-gray-700">
                                    ${name}
                                </p>
                            </div>
                            <div>
                                <label class="${FIELD_LABEL_CLASSES} mb-1">
                                    Team${REQUIRED_MARK_HTML}
                                </label>
                                <div data-compliance-protected-team="${systemKey}"></div>
                            </div>
                            <div>
                                <label class="${FIELD_LABEL_CLASSES} mb-1"
                                    for="compliance-protected-order-${systemKey}">
                                    Order${REQUIRED_MARK_HTML}
                                </label>
                                <input id="compliance-protected-order-${systemKey}"
                                    data-compliance-protected-order required
                                    type="number" step="any"
                                    value="${escapeHTML(String(draft?.order ?? ""))}"
                                    class="w-full rounded-md border border-gray-200 px-2 py-1 text-sm">
                            </div>
                            <div>
                                <label class="${FIELD_LABEL_CLASSES} mb-1"
                                    for="compliance-protected-due-${systemKey}">
                                    Due soon${REQUIRED_MARK_HTML}
                                </label>
                                <input id="compliance-protected-due-${systemKey}"
                                    data-compliance-protected-due required
                                    type="number" min="0" max="365" step="1"
                                    value="${escapeHTML(String(draft?.dueSoonDays ?? ""))}"
                                    class="w-full rounded-md border border-gray-200 px-2 py-1 text-sm">
                            </div>
                        </div>
                    `;
                }).join("")}
            </div>
            <button type="button"
                data-compliance-protected-initialize
                class="mt-3 inline-flex min-h-10 w-full max-w-full items-center justify-center whitespace-normal rounded-md border border-gray-300 bg-gray-200 px-4 py-2 text-center text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:border-gray-400 hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500/60 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500 disabled:opacity-60 disabled:shadow-none disabled:hover:bg-gray-100 sm:w-auto"
                ${getTeamOptions().length ? "" : "disabled"}>
                Initialise protected categories
            </button>
        </section>
    `;
}

function renderContent(container, generation) {
    destroyRegisteredControlsIn(container);
    if (!tempConfigData) {
        container.innerHTML = `
            <div class="max-w-5xl rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                Compliance Settings could not be loaded.
            </div>
        `;
        return;
    }
    const hasTeams = Array.isArray(appData.teams) && appData.teams.length > 0;
    container.innerHTML = `
        <section class="w-full max-w-5xl" data-compliance-settings>
            <div class="rounded-lg bg-white p-5 shadow-md sm:p-6">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h3 class="text-lg font-semibold text-gray-900">Compliance categories</h3>
                        <p class="mt-1 max-w-2xl text-sm text-gray-500">
                            Categories control team access and the due-soon threshold used by Compliance rollups.
                        </p>
                    </div>
                    ${renderToggle()}
                </div>
                ${hasTeams ? "" : `
                    <p class="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        Create a team before adding a Compliance category.
                    </p>
                `}
                ${renderProtectedCategoryInitialization()}
                <div class="mt-5 space-y-3" data-compliance-category-rows>
                    ${renderCategoryRows()}
                </div>
                <button type="button" data-compliance-category-add
                    class="settings-add-row-btn mt-3"
                    ${hasTeams && getDraftCategories().length < 50 ? "" : "disabled"}>
                    + Add Category
                </button>
                <p class="mt-2 text-xs text-gray-500">
                    Up to 50 categories. People and Assets cannot be renamed, archived, or deleted.
                </p>
            </div>
        </section>
    `;
    bindNativeFieldBehavior(container);
    const teamOptions = getTeamOptions();
    container.querySelectorAll("[data-compliance-protected-team]")
        .forEach((root) => {
            const systemKey = root.dataset.complianceProtectedTeam;
            const draft =
                protectedCategoryInitializationDrafts.get(systemKey);
            if (!draft) {return;}
            createSelectControl({
                root,
                name: `complianceProtectedTeam_${systemKey}`,
                value: draft.teamId,
                options: teamOptions,
                searchable: teamOptions.length >= 7,
                portalMenu: true,
                required: true,
                placeholder: "Select team",
                accessibleName:
                    `Team for ${systemKey === "people" ? "People" : "Assets"}`,
                onChange(value) {
                    draft.teamId = value;
                },
            });
        });
    container.querySelectorAll("[data-compliance-category-team]")
        .forEach((root) => {
            const category = findCategory(
                root.dataset.complianceCategoryTeamId,
            );
            if (!category) {return;}
            createSelectControl({
                root,
                name: `complianceTeam_${category.id}`,
                value: category.teamId || "",
                options: teamOptions,
                searchable: teamOptions.length >= 7,
                portalMenu: true,
                required: category.isActive === true,
                disabled: category.isActive !== true,
                placeholder: "Select team",
                accessibleName: `Team for ${category.name || "Compliance category"}`,
                onChange(value) {
                    category.teamId = value;
                    markDirty();
                },
            });
        });
    bindInteractions(container, generation);
}

function rerender(container, focusSelector = "") {
    const generation = ++renderGeneration;
    renderContent(container, generation);
    if (!focusSelector) {return;}
    requestAnimationFrame(() => {
        if (!ownsCurrentRender(container, generation)) {return;}
        container.querySelector(focusSelector)?.focus({preventScroll: true});
    });
}

function moveCategory(container, categoryId, direction) {
    const entries = getOrderedCategories().filter(({category}) =>
        category.isActive === true);
    const currentIndex = entries.findIndex(({category}) =>
        category.id === categoryId);
    const nextIndex = direction === "up" ?
        currentIndex - 1 :
        currentIndex + 1;
    if (currentIndex < 0 || nextIndex < 0 ||
        nextIndex >= entries.length) {return;}
    const current = entries[currentIndex];
    const next = entries[nextIndex];
    current.category.order = next.order;
    next.category.order = current.order;
    markDirty();
    rerender(
        container,
        `[data-compliance-category-id="${CSS.escape(categoryId)}"] ` +
        `[data-compliance-category-move="${direction}"]`,
    );
}

function removeLocalCategory(container, categoryId) {
    const categories = getDraftCategories();
    const index = categories.findIndex((entry) =>
        entry.id === categoryId);
    if (index < 0) {return;}
    categories.splice(index, 1);
    markDirty();
    rerender(container, "[data-compliance-category-add]");
}

function showLifecycleFailure(error) {
    const reason = error?.details?.reason || error?.data?.reason || "";
    const messages = {
        category_has_active_items:
            "Archive or move every active tracked item before archiving this category.",
        category_has_items:
            "Only a category with no tracked-item documents can be deleted.",
        category_not_found:
            "This Compliance category no longer exists. Reopen Settings to refresh it.",
        stale_expected_state:
            "Compliance category settings changed. Reopen Settings and try again.",
    };
    openConfirmationModal(
        "Compliance category",
        escapeHTML(messages[reason] || error?.message ||
            "The category could not be updated."),
        () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

async function applyLifecycle({
    action,
    categoryId,
    confirmEmptyArchive = false,
    container,
    generation,
}) {
    const baseline = getBaselineCategories().find((entry) =>
        entry.id === categoryId);
    if (!baseline) {return;}
    try {
        const response = await changeComplianceCategoryLifecycle({
            action,
            categoryId,
            expectedCategoryState: buildExpectedCategoryState(baseline),
            ...(confirmEmptyArchive ? {confirmEmptyArchive: true} : {}),
        });
        reconcileAuthoritativeCategories(response.categories, {
            action,
            categoryId,
        });
        if (!ownsCurrentRender(container, generation)) {return;}
        if (response.outcome === EMPTY_USED_CATEGORY_CHOICE_REQUIRED) {
            const category = findCategory(categoryId) || baseline;
            openConfirmationModal(
                "Empty Compliance category",
                `<strong>${escapeHTML(category.name)}</strong> has no tracked items. Delete it permanently or archive it for later use?`,
                () => applyLifecycle({
                    action: "archive",
                    categoryId,
                    confirmEmptyArchive: true,
                    container,
                    generation,
                }),
                false,
                "Archive",
                null,
                "Archiving...",
                "Cancel",
                {
                    closeOnError: false,
                    leftAction: {
                        buttonText: "Delete category",
                        loadingText: "Deleting...",
                        onClick: () => applyLifecycle({
                            action: "delete",
                            categoryId,
                            container,
                            generation,
                        }),
                        placement: "beforeConfirm",
                        variant: "dangerSecondary",
                    },
                },
            );
            return;
        }
        rerender(container, "[data-compliance-category-add]");
    } catch (error) {
        setTimeout(() => showLifecycleFailure(error), 0);
        throw error;
    }
}

function confirmLifecycle(container, generation, category, action) {
    if (!isPersisted(category.id)) {
        if (action === "delete") {
            removeLocalCategory(container, category.id);
        }
        return;
    }
    const label = {
        archive: "Archive",
        restore: "Unarchive",
        delete: "Delete",
    }[action];
    openConfirmationModal(
        `${label} Compliance category?`,
        `${label} <strong>${escapeHTML(category.name)}</strong>?`,
        () => applyLifecycle({
            action,
            categoryId: category.id,
            container,
            generation,
        }),
        action === "delete",
        label,
        null,
        `${label}...`,
    );
}

function showProtectedInitializationFailure(error) {
    const reason = error?.details?.reason || error?.data?.reason || "";
    const message = reason === "protected_category_conflict" ?
        "A manual or conflicting People/Assets category already exists. " +
            "Rename or repair that row before initialising the fixed category." :
        error?.message ||
            "The protected Compliance categories could not be initialised.";
    openConfirmationModal(
        "Protected Compliance categories",
        escapeHTML(message),
        () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

async function initializeMissingProtectedCategories(container, generation) {
    const section = container.querySelector(
        "[data-compliance-protected-initialization]",
    );
    if (!section) {return;}
    const nativeValid = validateNativeFormFields(section);
    const controlValidation = validateRegisteredControls(section);
    if (!nativeValid || !controlValidation.valid) {
        if (nativeValid) {
            focusFirstRegisteredControlError(controlValidation);
        }
        return;
    }
    const missing = getMissingProtectedCategoryDefinitions();
    const categories = missing.map(({systemKey}) => {
        const draft = protectedCategoryInitializationDrafts.get(systemKey);
        return {
            systemKey,
            teamId: String(draft?.teamId || "").trim(),
            order: Number(draft?.order),
            dueSoonDays: Number(draft?.dueSoonDays),
        };
    });
    if (categories.some((category) =>
        !category.teamId ||
        !Number.isFinite(category.order) ||
        !Number.isInteger(category.dueSoonDays) ||
        category.dueSoonDays < 0 ||
        category.dueSoonDays > 365)) {
        showProtectedInitializationFailure(new Error(
            "Each missing category needs an explicit team, finite order, and " +
            "whole-number due-soon value from 0 through 365.",
        ));
        return;
    }
    const button = section.querySelector(
        "[data-compliance-protected-initialize]",
    );
    if (button) {
        button.disabled = true;
        button.textContent = "Initialising...";
    }
    try {
        const response = await initializeComplianceProtectedCategories({
            categories,
        });
        reconcileAuthoritativeCategories(response.categories);
        response.createdSystemKeys?.forEach((systemKey) => {
            protectedCategoryInitializationDrafts.delete(systemKey);
        });
        if (ownsCurrentRender(container, generation)) {
            rerender(container);
        }
    } catch (error) {
        if (ownsCurrentRender(container, generation) && button) {
            button.disabled = false;
            button.textContent = "Initialise protected categories";
        }
        showProtectedInitializationFailure(error);
    }
}

function bindInteractions(container, generation) {
    container.querySelectorAll("[data-compliance-protected-row]")
        .forEach((row) => {
            const draft = protectedCategoryInitializationDrafts.get(
                row.dataset.complianceProtectedRow,
            );
            if (!draft) {return;}
            row.querySelector("[data-compliance-protected-order]")
                ?.addEventListener("input", (event) => {
                    draft.order = event.currentTarget.value;
                });
            row.querySelector("[data-compliance-protected-due]")
                ?.addEventListener("input", (event) => {
                    draft.dueSoonDays = event.currentTarget.value;
                });
        });
    container.querySelector("[data-compliance-protected-initialize]")
        ?.addEventListener("click", () => {
            void initializeMissingProtectedCategories(container, generation);
        });
    container.querySelector("[data-compliance-settings-show-inactive]")
        ?.addEventListener("change", (event) => {
            showInactive = event.currentTarget.checked;
            rerender(
                container,
                "[data-compliance-settings-show-inactive]",
            );
        });
    container.querySelector("[data-compliance-category-add]")
        ?.addEventListener("click", () => {
            const categories = ensureDraftCategories();
            const teams = Array.isArray(appData.teams) ? appData.teams : [];
            if (!categories || !teams.length || categories.length >= 50) {
                return;
            }
            const id = `draft_${crypto.randomUUID()}`;
            const nextOrder = Math.max(
                -1,
                ...categories.map((entry, index) =>
                    Number.isFinite(Number(entry?.order)) ?
                        Number(entry.order) :
                        index),
            ) + 1;
            categories.push({
                id,
                name: "",
                order: nextOrder,
                isActive: true,
                hasBeenUsed: false,
                kind: "manual",
                systemKey: null,
                teamId: teams[0].id,
                dueSoonDays: 30,
            });
            markDirty();
            rerender(
                container,
                `[data-compliance-category-id="${CSS.escape(id)}"] ` +
                "[data-compliance-category-name]",
            );
        });
    container.querySelectorAll("[data-compliance-category-name]")
        .forEach((input) => input.addEventListener("input", () => {
            const categoryId = input.closest("[data-compliance-category-id]")
                ?.dataset.complianceCategoryId;
            const category = findCategory(categoryId);
            if (!category) {return;}
            category.name = input.value;
            markDirty();
        }));
    container.querySelectorAll("[data-compliance-category-due]")
        .forEach((input) => input.addEventListener("input", () => {
            const categoryId = input.closest("[data-compliance-category-id]")
                ?.dataset.complianceCategoryId;
            const category = findCategory(categoryId);
            if (!category) {return;}
            category.dueSoonDays = input.value === "" ?
                "" :
                Number(input.value);
            markDirty();
        }));
    container.querySelectorAll("[data-compliance-category-move]")
        .forEach((button) => button.addEventListener("click", () => {
            moveCategory(
                container,
                button.dataset.complianceCategoryId,
                button.dataset.complianceCategoryMove,
            );
        }));
    container.querySelectorAll("[data-compliance-category-action]")
        .forEach((button) => button.addEventListener("click", () => {
            if (!validateComplianceSettingsControls(container)) {return;}
            const categoryId = button.closest("[data-compliance-category-id]")
                ?.dataset.complianceCategoryId;
            const category = findCategory(categoryId);
            if (!category) {return;}
            confirmLifecycle(
                container,
                generation,
                category,
                button.dataset.complianceCategoryAction,
            );
        }));
}

function validateComplianceSettingsControls(container) {
    const nativeValid = validateNativeFormFields(container);
    const controlValidation = validateRegisteredControls(container);
    if (!nativeValid || !controlValidation.valid) {
        if (nativeValid) {
            focusFirstRegisteredControlError(controlValidation);
        }
        return false;
    }
    return true;
}

function showSettingsFailure(error) {
    openConfirmationModal(
        "Compliance categories",
        escapeHTML(error?.message ||
            "Compliance category settings could not be saved."),
        () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

/**
 * Validates the staged category participant before the shared Settings save.
 * @returns {boolean} Whether the draft is valid.
 */
export function validateComplianceSettingsDraft() {
    const container = document.querySelector("[data-compliance-settings]");
    if (container && !validateComplianceSettingsControls(container)) {
        return false;
    }
    const categories = getDraftCategories();
    const invalid = categories.find((category) => {
        const due = Number(category?.dueSoonDays);
        return !String(category?.name || "").trim() ||
            !String(category?.teamId || "").trim() ||
            !Number.isInteger(due) ||
            due < 0 ||
            due > 365;
    });
    if (invalid) {
        openConfirmationModal(
            "Compliance categories",
            "Every category needs a name, team, and whole-number due-soon value from 0 through 365.",
            () => {},
            false,
            "OK",
            null,
            "",
            "",
            {hideCancel: true},
        );
        return false;
    }
    if (categories.length > 50) {
        openConfirmationModal(
            "Compliance categories",
            "Compliance supports at most 50 categories.",
            () => {},
            false,
            "OK",
            null,
            "",
            "",
            {hideCancel: true},
        );
        return false;
    }
    return true;
}

/**
 * Flushes only staged Compliance category edits through the authoritative
 * callable boundary, rebasing after every response while preserving all other
 * Settings drafts.
 * @returns {Promise<boolean>} Whether the participant saved successfully.
 */
export async function saveComplianceSettingsDraft() {
    if (!validateComplianceSettingsDraft()) {return false;}
    getDraftCategories().forEach((category) => {
        category.dueSoonDays = Number(category.dueSoonDays);
    });
    try {
        const draftOrder = getOrderedCategories()
            .map(({category}) => category.id);
        for (const draftId of draftOrder) {
            const draft = findCategory(draftId);
            if (!draft) {continue;}
            const baseline = getBaselineCategories().find((entry) =>
                entry.id === draftId);
            if (baseline && snapshotsEqual(
                editableCategorySnapshot(draft),
                editableCategorySnapshot(baseline),
            )) {
                continue;
            }
            const beforeIds = new Set(getBaselineCategories().map((entry) =>
                entry.id));
            const response = await saveComplianceCategoryConfig({
                categoryId: baseline ? baseline.id : null,
                expectedCategoryState: baseline ?
                    buildExpectedCategoryState(baseline) :
                    null,
                ...editableCategorySnapshot(draft),
            });
            let createdId = "";
            if (!baseline) {
                createdId = (response.categories || [])
                    .find((entry) => !beforeIds.has(entry.id))?.id || "";
                if (!createdId) {
                    throw new Error(
                        "The new Compliance category could not be identified.",
                    );
                }
            }
            reconcileAuthoritativeCategories(response.categories, {
                temporaryId: baseline ? "" : draftId,
                createdId,
            });
        }
        return true;
    } catch (error) {
        showSettingsFailure(error);
        return false;
    }
}

/**
 * Clears display-only Compliance Settings state when leaving Settings.
 * @returns {void}
 */
export function discardComplianceSettingsDraft() {
    const container = document.querySelector("[data-compliance-settings]");
    if (container) {destroyRegisteredControlsIn(container);}
    showInactive = false;
    protectedCategoryInitializationDrafts = new Map();
    renderGeneration += 1;
}

/**
 * Invalidates the current Settings render token.
 * @returns {void}
 */
export function invalidateComplianceSettingsRender() {
    const container = document.querySelector("[data-compliance-settings]");
    if (container) {destroyRegisteredControlsIn(container);}
    renderGeneration += 1;
}

/**
 * Renders the config-backed Compliance Settings participant.
 * @param {HTMLElement} container Settings tab container.
 * @param {{isCurrent?: Function, updateSaveButton?: Function}} [options] Dependencies.
 * @returns {void}
 */
export function renderComplianceSettings(container, options = {}) {
    if (!container) {return;}
    isCurrentSettingsOwner = options.isCurrent || (() => true);
    updateSharedSaveButton = options.updateSaveButton || (() => {});
    const generation = ++renderGeneration;
    renderContent(container, generation);
}
